import {
  StepIcon,
  StepIconProps,
  StepLabel,
  styled,
  SvgIcon,
  SvgIconProps,
  StepLabelProps,
  Theme
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';

export interface SideStepLabelProps extends StepLabelProps {
  label: string;
  completed?: boolean;
  readOnly?: boolean;
}

const iconFontSize = 28;
/** Abbott Check Circle */
const CheckCircle = ({ ...props }: SvgIconProps) => (
  <SvgIcon
    data-testid="CheckCircleIcon"
    {...props}
    sx={{ fontSize: iconFontSize }}
  >
    <svg
      {...props}
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 24 24"
      width="100%"
      height="100%"
    >
      <style type="text/css">
        {'\n\t.st0{fill:currentColor;}\n\t.st1{fill:none;}\n'}
      </style>
      <polygon
        className="st0"
        points="10.1,13.2 7.2,10.3 5.1,12.4 10.1,17.4 12.2,15.3 18.3,9.1 16.3,7  "
      />
      <path
        className="st0"
        d="M12,0.5C5.6,0.5,0.3,5.8,0.3,12.2S5.6,23.9,12,23.9s11.7-5.2,11.7-11.7S18.4,0.5,12,0.5z M12,21.5 c-5.1,0-9.3-4.2-9.3-9.3S6.9,2.9,12,2.9s9.3,4.2,9.3,9.3S17.1,21.5,12,21.5z"
      />
    </svg>
  </SvgIcon>
);

const StepIconText = styled('text')(({ theme }: { theme: Theme }) => ({
  fontSize: theme.typography.caption['fontSize'],
  fontFamily: theme.typography.fontFamily
}));

interface DisabledIconInterface {
  svgIconProps?: SvgIconProps;
  stepIconProps: StepIconProps;
}

interface SidestepIconProps {
  stepIconProps: StepIconProps;
  readOnly?: boolean;
}

/** Abbott Disabled Icon with icon text number */
const DisabledIcon = ({
  stepIconProps,
  svgIconProps = {}
}: DisabledIconInterface) => (
  <SvgIcon sx={{ fontSize: iconFontSize }} {...svgIconProps}>
    <svg
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      viewBox="0 0 24 24"
      width="100%"
      height="100%"
      data-testid="DisabledIcon"
    >
      <style type="text/css">
        {'\n\t.st0{fill:currentColor;}\n\t.st1{fill:none;}\n'}
      </style>
      <path
        className="st0"
        d="M12,0.5C5.6,0.5,0.3,5.8,0.3,12.2S5.6,23.9,12,23.9s11.7-5.2,11.7-11.7S18.4,0.5,12,0.5z M12,21.5 c-5.1,0-9.3-4.2-9.3-9.3S6.9,2.9,12,2.9s9.3,4.2,9.3,9.3S17.1,21.5,12,21.5z"
      />
      <StepIconText
        x="12"
        y="16"
        textAnchor="middle"
        color="currentColor"
        fontWeight="bold"
        className="st0"
      >
        {stepIconProps.icon}
      </StepIconText>
    </svg>
  </SvgIcon>
);

DisabledIcon.defaultProps = {
  svgIconProps: {}
};

const CurrentReadOnlyIcon = styled(VisibilityIcon)(({ theme }) => ({
  fill: 'white',
  backgroundColor: 'currentColor',
  borderRadius: '50%',
  padding: theme.spacing(1),
  fontSize: '2rem', // Adjust the size as needed
  boxSizing: 'content-box',
  width: '16px',
  height: '16px'
}));

const OtherReadOnlyIcon = styled(VisibilityIcon)(({ theme }) => ({
  fill: 'currentColor',
  backgroundColor: 'white',
  borderRadius: '50%',
  padding: theme.spacing(1),
  fontSize: '2rem', // Adjust the size as needed
  boxSizing: 'content-box',
  width: '14px',
  height: '14px',
  border: '2px solid currentColor'
}));

/** Swap out appropriate icon */
const SideStepIcon = (props: SidestepIconProps) => {
  const { stepIconProps, readOnly } = props;
  const { active, error, completed } = stepIconProps;
  if (readOnly) {
    if (active) {
      return (
        <StepIcon {...props} icon={<CurrentReadOnlyIcon color="primary" />} />
      );
    } else {
      return (
        <StepIcon {...props} icon={<OtherReadOnlyIcon color="primary" />} />
      );
    }
  }
  if (error) {
    return (
      <StepIcon
        {...props}
        icon={<ErrorOutlineIcon color="error" />}
        completed={false}
      />
    );
  }
  if (completed) {
    return (
      <StepIcon
        {...props}
        icon={<CheckCircle color="primary" />}
        error={false}
      />
    );
  }

  if (active) {
    return (
      <StepIcon
        {...stepIconProps}
        sx={{ fontSize: iconFontSize }}
        error={false}
      />
    );
  }

  return (
    <StepIcon
      {...stepIconProps}
      error={false}
      icon={
        <DisabledIcon
          svgIconProps={{ color: 'primary' }}
          stepIconProps={props.stepIconProps}
        />
      }
    />
  );
};

export const SideStepLabel = ({
  label,
  completed,
  readOnly,
  ...props
}: SideStepLabelProps) => (
  <StepLabel
    {...props}
    error={readOnly ? false : props.error}
    StepIconComponent={(props) => (
      <SideStepIcon
        stepIconProps={{ ...props, completed: completed }}
        readOnly={readOnly}
      />
    )}
  >
    {label}
  </StepLabel>
);

export default SideStepLabel;
